import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { getFormattedDate, handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { useDeleteClient, useGetClients } from "../api-clients";
import { ClientType } from "./client-types";
import useClientTableColumns from "./useClientTableColumns";

const ClientsList = () => {
  useSetActiveTab(TAB_NAMES.CLIENT_MANAGEMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: updateClientData,
    isValidating,
  } = useGetClients({ length, page, search: debouncedSearch });

  const { isRefreshing, handleRefresh } = useManualRefresh(updateClientData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteClient,
  } = useDeleteClient();

  const clientData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const mappedClientsData = useMemo(() => {
    return clientData.map((client: ClientType) => {
      return {
        ...client,
        dob: client.dob ? getFormattedDate(client.dob) : "",
        createdAt: getFormattedDate(client.createdAt),
        updatedAt: getFormattedDate(client.updatedAt),
      };
    });
  }, [clientData]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useClientTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateClientData();
      setIsDeleteModalOpen(false);
      successNotification("Client Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateClientData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteClient(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Client Management"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(mappedClientsData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Client",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={mappedClientsData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default ClientsList;
